/**
 * styleHelpers.js
 * Shared style utility functions extracted from App.jsx.
 * These are pure functions — no React, no state, no side-effects.
 * Import what you need and pass in the `settings` / `typography` objects.
 */

// ─── STRIP HTML / PLAIN TEXT HELPER ─────────────────────────────────────────
export const stripHtml = (html) => {
    if (!html || typeof html !== 'string') return html || '';
    return html
        .replace(/<[^>]*>/g, '')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#039;/g, "'")
        .replace(/&apos;/g, "'")
        .replace(/&nbsp;/gi, ' ')
        .trim();
};

// ─── UNIT NORMALISER ─────────────────────────────────────────────────────────
export const addUnit = (val) => {
    if (val === undefined || val === null || val === '') return '';
    const str = String(val).trim();
    if (str === '0') return '0px';
    if (['auto', 'none', 'inherit', 'initial', 'unset', 'transparent'].includes(str)) return str;
    if (['px', 'rem', 'em', '%', 'vh', 'vw', 'pt', 's', 'ms'].some(u => str.toLowerCase().endsWith(u))) return str;
    if (!isNaN(str) && str !== '') {
        const num = parseFloat(str);
        return num > 8 ? `${num}px` : `${num}rem`;
    }
    return `${str}px`;
};

// ─── FONT STYLES ─────────────────────────────────────────────────────────────
// ─── CSS STRING PARSER ────────────────────────────────────────────────────────
export const parseCssStringToStyle = (cssString) => {
    if (!cssString || typeof cssString !== 'string') return {};
    const style = {};
    cssString.split(';').forEach(rule => {
        const colonIdx = rule.indexOf(':');
        if (colonIdx > -1) {
            const prop = rule.slice(0, colonIdx).trim();
            const val = rule.slice(colonIdx + 1).trim();
            if (prop && val) {
                const cleanVal = val.replace(/\s*!important\s*$/i, '');
                let camelProp;
                if (prop.startsWith('-webkit-') || prop.startsWith('-moz-') || prop.startsWith('-ms-')) {
                    const cleanPrefix = prop.startsWith('-') ? prop.slice(1) : prop;
                    camelProp = cleanPrefix.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
                } else if (prop.startsWith('-')) {
                    camelProp = prop.slice(1).replace(/-([a-z])/g, (_, c) => c.toUpperCase());
                } else {
                    camelProp = prop.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
                }
                style[camelProp] = cleanVal;
            }
        }
    });
    return style;
};

// ─── CANONICAL TEXT SHADOW PRESETS ──────────────────────────────────────────
export const TEXT_SHADOW_PRESETS = {
    none: 'none',
    subtle: '1px 1px 2px rgba(0,0,0,0.5)',
    medium: '2px 2px 5px rgba(0,0,0,0.7)',
    hard: '3px 3px 0px rgba(0,0,0,0.85)',
    softGlow: '0 0 10px rgba(255,255,255,0.8), 0 0 20px rgba(59,130,246,0.6)',
    darkGlow: '0 0 14px rgba(0,0,0,0.9)',
    retro3D: '1px 1px 0 #000, 2px 2px 0 #000, 3px 3px 0 #000, 4px 4px 0 #000, 5px 5px 8px rgba(0,0,0,0.6)',
    neonCyan: '0 0 5px #06b6d4, 0 0 10px #06b6d4, 0 0 20px #0891b2, 0 0 40px #0e7490',
    neonMagenta: '0 0 5px #f43f5e, 0 0 10px #ec4899, 0 0 20px #d946ef, 0 0 40px #8b5cf6',
    neonGold: '0 0 5px #fbbf24, 0 0 15px #f59e0b, 0 0 30px #d97706',
    cinemaBackdrop: '0 10px 30px rgba(0,0,0,0.85), 0 2px 6px rgba(0,0,0,0.9)',
    letterpress: '0 1px 0 rgba(255,255,255,0.4), 0 -1px 0 rgba(0,0,0,0.7)',
    glitchDual: '-2px -2px 0 #06b6d4, 2px 2px 0 #ec4899, 0 0 12px rgba(0,0,0,0.8)',
    multiDepth: '0 1px 1px rgba(0,0,0,0.15), 0 2px 2px rgba(0,0,0,0.15), 0 4px 4px rgba(0,0,0,0.15), 0 8px 8px rgba(0,0,0,0.15), 0 16px 16px rgba(0,0,0,0.15)',
    outlineStroke: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000, 0 4px 8px rgba(0,0,0,0.5)'
};

export const convertTextShadowToDropShadow = (shadowStr) => {
    if (!shadowStr || shadowStr === 'none') return '';
    const layers = shadowStr.split(/,(?![^(]*\))/).map(s => s.trim()).filter(Boolean);
    return layers.map(layer => `drop-shadow(${layer})`).join(' ');
};

// ─── FONT STYLES ─────────────────────────────────────────────────────────────
export const getFontStyles = (typography) => (type, overrides = {}) => {
    const preset = overrides.preset || type;
    let rawSize = overrides.fontSize || overrides.size || typography[preset]?.size;
    if (rawSize) {
        const str = String(rawSize).trim().toLowerCase();
        if (str === '0' || str === '0px' || str === '0rem' || str === '0em') {
            rawSize = '1px';
        }
    }
    const size = (rawSize && !isNaN(rawSize)) ? (parseFloat(rawSize) > 8 ? `${rawSize}px` : `${rawSize}rem`) : rawSize;

    // Resolve Text Shadow with strict preset precedence
    const shadowPreset = overrides.textShadowPreset || overrides.titleTextShadowPreset || overrides.subtitleTextShadowPreset || overrides.shadowPreset;
    const shadowCustom = overrides.textShadowCustom || overrides.titleTextShadowCustom || overrides.subtitleTextShadowCustom || overrides.shadowCustom;
    let textShadow = overrides.textShadow || overrides.titleTextShadow || overrides.subtitleTextShadow || overrides.titleShadow || overrides.subtitleShadow;

    if (shadowPreset && shadowPreset !== 'none') {
        if (shadowPreset === 'custom') {
            textShadow = shadowCustom || 'none';
        } else if (TEXT_SHADOW_PRESETS[shadowPreset]) {
            textShadow = TEXT_SHADOW_PRESETS[shadowPreset];
        }
    } else if (shadowPreset === 'none') {
        textShadow = 'none';
    } else if (!textShadow && shadowCustom) {
        textShadow = shadowCustom;
    }

    // Parse custom CSS declarations first for inheritance/composition
    const customCssStyles = parseCssStringToStyle(overrides.customCss || overrides.titleCustomCss || overrides.subtitleCustomCss);

    // Resolve Text Gradient
    let gradientStyle = {};
    const textGradMode = overrides.textGradientMode || overrides.gradientMode || overrides.titleTextGradientMode || overrides.subtitleTextGradientMode;
    const isGradient = textGradMode === 'gradient' || textGradMode === 'linear' || textGradMode === true || textGradMode === 'true';
    if (isGradient) {
        const start = overrides.textGradientStart || overrides.titleTextGradientStart || overrides.subtitleTextGradientStart || overrides.gradientStart || '#3b82f6';
        const end = overrides.textGradientEnd || overrides.titleTextGradientEnd || overrides.subtitleTextGradientEnd || overrides.gradientEnd || '#9333ea';
        const rawAngle = overrides.textGradientAngle || overrides.titleTextGradientAngle || overrides.subtitleTextGradientAngle || overrides.gradientAngle || '135';
        const angle = String(rawAngle).replace(/deg$/i, '');
        gradientStyle = {
            backgroundImage: `linear-gradient(${angle}deg, ${start}, ${end})`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            color: 'transparent',
            width: overrides.width || '100%',
            maxWidth: '100%',
            ...(overrides.display ? { display: overrides.display } : {})
        };

        // On gradient text, browsers clip background to glyphs so text-shadow is suppressed or drawn on top.
        // We synthesize the shadow via CSS filter drop-shadow to guarantee rendering across Chrome/WebKit/Safari.
        if (textShadow && textShadow !== 'none') {
            const dropShadowFilter = convertTextShadowToDropShadow(textShadow);
            if (dropShadowFilter) {
                gradientStyle.filter = customCssStyles.filter ? `${customCssStyles.filter} ${dropShadowFilter}` : dropShadowFilter;
                gradientStyle.WebkitFilter = gradientStyle.filter;
            }
        }
    }

    const resolvedColor = gradientStyle.backgroundImage
        ? 'transparent'
        : (overrides.color || overrides.titleColor || overrides.subtitleColor || overrides.textColor || typography[preset]?.color);

    // Text stroke / outline
    let strokeStyle = {};
    const strokeWidth = overrides.textStrokeWidth || overrides.titleTextStrokeWidth || overrides.subtitleTextStrokeWidth;
    const strokeColor = overrides.textStrokeColor || overrides.titleTextStrokeColor || overrides.subtitleTextStrokeColor || '#000000';
    if (strokeWidth && strokeWidth !== '0' && strokeWidth !== '0px') {
        const fullStroke = `${addUnit(strokeWidth)} ${strokeColor}`;
        strokeStyle = {
            WebkitTextStroke: fullStroke,
            textStroke: fullStroke
        };
    }

    return {
        fontFamily: overrides.fontFamily || typography[preset]?.fontFamily || typography.fontFamily || 'sans-serif',
        fontSize: size,
        fontWeight: (overrides.fontWeight !== undefined && overrides.fontWeight !== '')
            ? overrides.fontWeight
            : ((overrides.weight !== undefined && overrides.weight !== '')
                ? overrides.weight
                : typography[preset]?.fontWeight),
        fontStyle: overrides.fontStyle || typography[preset]?.fontStyle,
        textDecoration: overrides.textDecoration || typography[preset]?.textDecoration,
        letterSpacing: (overrides.letterSpacing !== undefined && overrides.letterSpacing !== '')
            ? (typeof overrides.letterSpacing === 'number' || (!isNaN(overrides.letterSpacing) && String(overrides.letterSpacing).trim() !== ''))
                ? `${overrides.letterSpacing}px`
                : overrides.letterSpacing
            : typography[preset]?.letterSpacing,
        textTransform: (() => {
            const explicitTransform = overrides.textTransform ?? overrides.transform ?? overrides.headingTransform ?? overrides.titleTransform ?? overrides.subtitleTransform;
            if (explicitTransform !== undefined && explicitTransform !== '') return explicitTransform;
            return typography[preset]?.textTransform || 'none';
        })(),
        color: resolvedColor,
        lineHeight: overrides.lineHeight || typography[preset]?.lineHeight,
        textAlign: overrides.textAlign || overrides.align || typography[preset]?.textAlign || 'inherit',
        textShadow: isGradient
            ? 'none'
            : ((textShadow && textShadow !== 'none') ? textShadow : (shadowPreset === 'none' || textShadow === 'none' ? 'none' : undefined)),
        ...gradientStyle,
        ...strokeStyle,
        ...customCssStyles
    };
};

// ─── SHADOW ───────────────────────────────────────────────────────────────────
export const getShadow = (settings = {}, maybePrefix) => {
    const resolveForPrefix = (prefix) => {
        if (!settings || typeof settings !== 'object') return 'none';
        const preset = settings[`${prefix}ShadowPreset`];
        const custom = settings[`${prefix}ShadowCustom`] || settings[`${prefix}CustomShadow`];
        if (preset === 'none') return 'none';
        if (preset === 'subtle') return '0 2px 8px rgba(0,0,0,0.12)';
        if (preset === 'medium') return '0 4px 16px rgba(0,0,0,0.2)';
        if (preset === 'hard') return '0 8px 28px rgba(0,0,0,0.35)';
        if (preset === 'softGlow') return '0 0 15px rgba(59,130,246,0.4)';
        if (preset === 'darkGlow') return '0 0 20px rgba(0,0,0,0.7)';
        if (preset === 'custom' && custom) return custom;
        if (custom) return custom;

        const color = settings[`${prefix}ShadowColor`];
        if (!color || color === 'transparent') return 'none';
        const x = addUnit(settings[`${prefix}ShadowX`] || '0px');
        const y = addUnit(settings[`${prefix}ShadowY`] || '0px');
        const blur = addUnit(settings[`${prefix}ShadowBlur`] || '0px');
        const spread = addUnit(settings[`${prefix}ShadowSpread`] || '0px');
        const inset = settings[`${prefix}ShadowInset`] ? 'inset ' : '';
        if (x === '0px' && y === '0px' && blur === '0px' && spread === '0px') return 'none';
        return `${inset}${x} ${y} ${blur} ${spread !== '0px' ? spread + ' ' : ''}${color}`;
    };

    if (typeof maybePrefix === 'string') {
        return resolveForPrefix(maybePrefix);
    }
    return resolveForPrefix;
};

// ─── BORDERS ──────────────────────────────────────────────────────────────────
export const getBorder = (settings = {}, maybePrefix) => {
    const resolveForPrefix = (prefix) => {
        if (!settings || typeof settings !== 'object') return 'none';
        const width = addUnit(settings[`${prefix}BorderWidth`]);
        const color = settings[`${prefix}BorderColor`] || 'transparent';
        const style = settings[`${prefix}BorderStyle`] || 'solid';
        if (width === '0px' || color === 'transparent' || style === 'none') return 'none';
        return `${width} ${style} ${color}`;
    };

    if (typeof maybePrefix === 'string') {
        return resolveForPrefix(maybePrefix);
    }
    return resolveForPrefix;
};

export const getSideBorder = (settings = {}, maybePrefix) => {
    const resolveForPrefix = (prefix) => {
        if (!settings || typeof settings !== 'object') return 'none';
        const width = addUnit(settings[`${prefix}Width`]);
        const color = settings[`${prefix}Color`] || 'transparent';
        const style = settings[`${prefix}Style`] || 'solid';
        if (width === '0px' || color === 'transparent' || style === 'none') return 'none';
        return `${width} ${style} ${color}`;
    };

    if (typeof maybePrefix === 'string') {
        return resolveForPrefix(maybePrefix);
    }
    return resolveForPrefix;
};

// ─── BACKGROUND ───────────────────────────────────────────────────────────────
/**
 * Builds a background CSS value for a given prefix.
 * Supports: solid, gradient (2-stop), gradient with optional mid-stop.
 * When the gradient is scroll-animated, a larger background is used so
 * JS can shift background-position on scroll. The actual animation is
 * handled by the GradientAnimator component in App.jsx.
 */
export const getBackground = (settings) => (prefix, fallbackSolid) => {
    const mode = settings[`${prefix}BgMode`];
    if (mode === 'gradient') {
        const start = settings[`${prefix}GradientStart`] || '#ffffff';
        const mid = settings[`${prefix}GradientMid`] || null;
        const end = settings[`${prefix}GradientEnd`] || '#000000';
        const angle = settings[`${prefix}GradientAngle`] || '180';
        const stops = mid
            ? `${start}, ${mid}, ${end}`
            : `${start}, ${end}`;
        return `linear-gradient(${angle}deg, ${stops})`;
    }
    let color = fallbackSolid;
    if (color === 'primary') color = settings.primaryColor;
    else if (color === 'secondary') color = settings.secondaryColor;
    else if (color === 'tertiary') color = settings.tertiaryColor;
    return color || '#ffffff';
};

export const resolveSectionBg = (settings) => (bg) => {
    const _getBackground = getBackground(settings);
    if (bg === 'primary') return _getBackground('primary', settings.primaryColor);
    if (bg === 'secondary') return settings.secondaryColor;
    if (bg === 'tertiary') return settings.tertiaryColor;
    if (bg === 'pageBackground') return settings.pageBackground;
    return bg;
};

export const resolveSectionShadow = (outerPreset, outerCustom, innerPreset, innerCustom) => {
    let outerVal = 'none';
    if (outerPreset === 'sm') outerVal = '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)';
    else if (outerPreset === 'md') outerVal = '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)';
    else if (outerPreset === 'lg') outerVal = '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)';
    else if (outerPreset === 'xl') outerVal = '0 20px 25px -5px rgba(0,0,0,0.15), 0 10px 10px -5px rgba(0,0,0,0.08)';
    else if (outerPreset === '2xl') outerVal = '0 25px 50px -12px rgba(0,0,0,0.25)';
    else if (outerPreset === 'glow') outerVal = '0 0 20px rgba(59, 130, 246, 0.4)';
    else if (outerPreset === 'darkGlow') outerVal = '0 0 25px rgba(0,0,0,0.4)';
    else if (outerPreset === 'custom') outerVal = outerCustom || 'none';

    let innerVal = 'none';
    if (innerPreset === 'sm') innerVal = 'inset 0 2px 4px rgba(0,0,0,0.06)';
    else if (innerPreset === 'md') innerVal = 'inset 0 4px 8px rgba(0,0,0,0.12)';
    else if (innerPreset === 'lg') innerVal = 'inset 0 8px 16px rgba(0,0,0,0.18)';
    else if (innerPreset === 'vignette') innerVal = 'inset 0 0 20px rgba(0,0,0,0.3)';
    else if (innerPreset === 'topEdge') innerVal = 'inset 0 8px 8px -8px rgba(0,0,0,0.35)';
    else if (innerPreset === 'bottomEdge') innerVal = 'inset 0 -8px 8px -8px rgba(0,0,0,0.35)';
    else if (innerPreset === 'topBottom') innerVal = 'inset 0 8px 8px -8px rgba(0,0,0,0.35), inset 0 -8px 8px -8px rgba(0,0,0,0.35)';
    else if (innerPreset === 'custom') innerVal = innerCustom || 'none';

    if (outerVal !== 'none' && innerVal !== 'none') {
        return `${outerVal}, ${innerVal}`;
    }
    if (outerVal !== 'none') return outerVal;
    if (innerVal !== 'none') return innerVal;
    return undefined;
};

// ─── BUTTON COLOURS ───────────────────────────────────────────────────────────
export const getBtnColor = (settings = {}) => (variant) => {
    const _getBackground = getBackground(settings);
    if (variant === 'secondary') return _getBackground('secondary', settings.secondaryColor || '#10b981');
    if (variant === 'tertiary') return _getBackground('tertiary', settings.tertiaryColor || '#8b5cf6');
    if (variant === 'white') return '#ffffff';
    if (typeof variant === 'string' && (variant.startsWith('#') || variant.startsWith('rgb') || variant.startsWith('linear-gradient') || variant.startsWith('hsl'))) {
        return variant;
    }
    return _getBackground('primary', settings.primaryColor || '#3b82f6');
};

export const getBtnTextColor = (settings = {}) => (variant) => {
    if (variant === 'white') return '#333333';
    if (settings.buttonTextColor) return settings.buttonTextColor;
    return '#ffffff';
};

// ─── BUTTON BASE STYLE ────────────────────────────────────────────────────────
export const buildBtnStyleBase = (settings = {}, typography = {}) => {
    const _getFontStyles = getFontStyles(typography);
    const _getBorder = getBorder(settings);
    const _getShadow = getShadow(settings);

    const style = {
        borderRadius: addUnit(settings.buttonRadius),
        border: _getBorder('button'),
        boxShadow: _getShadow('button'),
        fontWeight: settings.buttonFontWeight || 'bold',
        fontSize: addUnit(settings.buttonFontSize || '1rem'),
        fontFamily: settings.buttonFontFamily || _getFontStyles('body').fontFamily,
        textTransform: settings.buttonTextTransform || 'none',
        letterSpacing: addUnit(settings.buttonLetterSpacing || 'normal'),
        padding: settings.buttonPadding || '12px 24px',
        display: settings.buttonDisplay || 'inline-block',
        textDecoration: settings.buttonTextDecoration || 'none',
        transition: settings.buttonTransition || 'all 0.25s ease',
        cursor: 'pointer'
    };

    if (settings.buttonTextColor) style.color = settings.buttonTextColor;
    if (settings.buttonHoverBg) style.buttonHoverBg = settings.buttonHoverBg;
    if (settings.buttonHoverTextColor) style.buttonHoverTextColor = settings.buttonHoverTextColor;
    if (settings.buttonMargin) style.margin = settings.buttonMargin;
    if (settings.buttonWidth && settings.buttonWidth !== 'auto') style.width = settings.buttonWidth;

    return style;
};

// ─── IMAGE FILTER & COLOR TINT RESOLVER ──────────────────────────────────────
export const resolveImageFilter = (cfg = {}) => {
    if (!cfg) return '';
    let filter = cfg.shopCardImageFilter || cfg.imageFilter || cfg.filter || cfg.bgFilter || '';
    if (filter === 'custom' && (cfg.shopCardImageCustomFilter || cfg.imageCustomFilter || cfg.bgCustomFilter)) {
        return (cfg.shopCardImageCustomFilter || cfg.imageCustomFilter || cfg.bgCustomFilter).trim();
    }
    if (filter && filter !== 'none' && filter !== 'custom') {
        return filter;
    }
    const parts = [];
    const desat = cfg.shopCardImageDesaturate !== undefined && cfg.shopCardImageDesaturate !== ''
        ? Number(cfg.shopCardImageDesaturate)
        : (cfg.imageDesaturate !== undefined && cfg.imageDesaturate !== '' 
            ? Number(cfg.imageDesaturate) 
            : (cfg.bgDesaturate !== undefined && cfg.bgDesaturate !== '' 
                ? Number(cfg.bgDesaturate) 
                : (cfg.backgroundDesaturate !== undefined && cfg.backgroundDesaturate !== '' 
                    ? Number(cfg.backgroundDesaturate) 
                    : 0)));
    if (!isNaN(desat) && desat > 0) {
        parts.push(`grayscale(${desat}%)`);
    } else if (cfg.imageGrayscale || cfg.bgGrayscale) {
        parts.push('grayscale(100%)');
    }
    if (cfg.imageSepia || cfg.bgSepia) {
        parts.push(`sepia(${cfg.imageSepia || cfg.bgSepia}%)`);
    }
    if (cfg.imageHueRotate || cfg.bgHueRotate) {
        parts.push(`hue-rotate(${cfg.imageHueRotate || cfg.bgHueRotate}deg)`);
    }
    return parts.join(' ');
};

// ─── IMAGE BASE STYLE ─────────────────────────────────────────────────────────
export const buildImgStyleBase = (settings) => {
    if (!settings) return {};
    const _getBorder = getBorder(settings);
    const _getShadow = getShadow(settings);
    const filter = resolveImageFilter(settings);
    const duration = settings.imageTransitionDuration || '0.4s';
    const timing = settings.imageTransitionTiming || 'cubic-bezier(0.4, 0, 0.2, 1)';

    return {
        borderRadius: addUnit(settings.imageRadius),
        border: _getBorder('image'),
        boxShadow: _getShadow('image'),
        padding: addUnit(settings.imagePadding),
        backgroundColor: settings.imagePadding && settings.imagePadding !== '0px' ? 'white' : 'transparent',
        display: 'block',
        ...(filter ? { filter, WebkitFilter: filter } : {}),
        transition: `filter ${duration} ${timing}, -webkit-filter ${duration} ${timing}, transform ${duration} ${timing}, opacity ${duration} ${timing}, box-shadow ${duration} ${timing}`,
    };
};

// ─── SAFE HEX COLOR ───────────────────────────────────────────────────────────
export const safeColor = (color) => {
    if (!color) return '#000000';
    if (color.startsWith('#') && (color.length === 4 || color.length === 7)) return color;
    return '#000000';
};

// ─── GALLERY STYLES GENERATOR ──────────────────────────────────────────────────
export const getGalleryStyles = (section) => {
    if (!section) return '';
    const secId = String(section.stableId || section.id || '').trim().replace(/^#/, '');
    const customId = String(section.customId || section.htmlId || section.elementId || section.sectionId || section.customAnchorId || '').trim().replace(/^#/, '');

    // Build resilient selector list matching class, data attribute, and ID variants
    const wrapperSelectors = [
        secId ? `.pgallery-sec-${secId}` : null,
        secId ? `[data-gallery-section="${secId}"]` : null,
        secId ? `[id="${secId}"] .pgallery-wrapper` : null,
        customId ? `[id="${customId}"] .pgallery-wrapper` : null,
        secId && /^[a-zA-Z_][a-zA-Z0-9_-]*$/.test(secId) ? `#${secId} .pgallery-wrapper` : null,
        customId && /^[a-zA-Z_][a-zA-Z0-9_-]*$/.test(customId) ? `#${customId} .pgallery-wrapper` : null,
    ].filter(Boolean).join(',\n');

    const itemSelectors = [
        secId ? `.pgallery-sec-${secId} .pgallery-item` : null,
        secId ? `.pgallery-sec-${secId} .pgallery-item-wrapper` : null,
        secId ? `[data-gallery-section="${secId}"] .pgallery-item` : null,
        secId ? `[data-gallery-section="${secId}"] .pgallery-item-wrapper` : null,
        secId ? `[id="${secId}"] .pgallery-item` : null,
        secId ? `[id="${secId}"] .pgallery-item-wrapper` : null,
        customId ? `[id="${customId}"] .pgallery-item` : null,
        customId ? `[id="${customId}"] .pgallery-item-wrapper` : null,
        secId ? `.pgallery-sec-${secId} > div` : null,
        secId ? `[data-gallery-section="${secId}"] > div` : null,
    ].filter(Boolean).join(',\n');

    const imgWrapSelectors = [
        secId ? `.pgallery-sec-${secId} .pgallery-img-wrap` : null,
        secId ? `[data-gallery-section="${secId}"] .pgallery-img-wrap` : null,
        secId ? `[id="${secId}"] .pgallery-img-wrap` : null,
        customId ? `[id="${customId}"] .pgallery-img-wrap` : null,
    ].filter(Boolean).join(',\n');

    const imgSelectors = [
        secId ? `.pgallery-sec-${secId} .pgallery-img` : null,
        secId ? `[data-gallery-section="${secId}"] .pgallery-img` : null,
        secId ? `[id="${secId}"] .pgallery-img` : null,
        customId ? `[id="${customId}"] .pgallery-img` : null,
    ].filter(Boolean).join(',\n');

    const hoverImgSelectors = [
        secId ? `.pgallery-sec-${secId} .pgallery-item:hover .pgallery-img` : null,
        secId ? `.pgallery-sec-${secId} .pgallery-item-wrapper:hover .pgallery-img` : null,
        secId ? `[data-gallery-section="${secId}"] .pgallery-item:hover .pgallery-img` : null,
        secId ? `[data-gallery-section="${secId}"] .pgallery-item-wrapper:hover .pgallery-img` : null,
        secId ? `[id="${secId}"] .pgallery-item:hover .pgallery-img` : null,
        secId ? `[id="${secId}"] .pgallery-item-wrapper:hover .pgallery-img` : null,
        customId ? `[id="${customId}"] .pgallery-item:hover .pgallery-img` : null,
        customId ? `[id="${customId}"] .pgallery-item-wrapper:hover .pgallery-img` : null,
    ].filter(Boolean).join(',\n');

    const captionOverlaySelectors = [
        secId ? `.pgallery-sec-${secId} .pgallery-caption-overlay` : null,
        secId ? `[data-gallery-section="${secId}"] .pgallery-caption-overlay` : null,
        secId ? `[id="${secId}"] .pgallery-caption-overlay` : null,
        customId ? `[id="${customId}"] .pgallery-caption-overlay` : null,
    ].filter(Boolean).join(',\n');

    const hoverCaptionOverlaySelectors = [
        secId ? `.pgallery-sec-${secId} .pgallery-item:hover .pgallery-caption-overlay` : null,
        secId ? `.pgallery-sec-${secId} .pgallery-item-wrapper:hover .pgallery-caption-overlay` : null,
        secId ? `[data-gallery-section="${secId}"] .pgallery-item:hover .pgallery-caption-overlay` : null,
        secId ? `[data-gallery-section="${secId}"] .pgallery-item-wrapper:hover .pgallery-caption-overlay` : null,
        secId ? `[id="${secId}"] .pgallery-item:hover .pgallery-caption-overlay` : null,
        secId ? `[id="${secId}"] .pgallery-item-wrapper:hover .pgallery-caption-overlay` : null,
        customId ? `[id="${customId}"] .pgallery-item:hover .pgallery-caption-overlay` : null,
        customId ? `[id="${customId}"] .pgallery-item-wrapper:hover .pgallery-caption-overlay` : null,
    ].filter(Boolean).join(',\n');

    const captionBelowSelectors = [
        secId ? `.pgallery-sec-${secId} .pgallery-caption-below` : null,
        secId ? `[data-gallery-section="${secId}"] .pgallery-caption-below` : null,
        secId ? `[id="${secId}"] .pgallery-caption-below` : null,
        customId ? `[id="${customId}"] .pgallery-caption-below` : null,
    ].filter(Boolean).join(',\n');

    const columns = section.galleryColumns || 3;
    const gap = section.galleryGap || '16px';
    const radius = section.galleryBorderRadius || '8px';
    const hover = section.galleryHoverEffect || 'scale';
    const shadow = section.galleryShadow || 'md';
    const ar = section.galleryAspectRatio || '1/1';

    const borderWidth = section.galleryBorderWidth || '0px';
    const borderStyle = section.galleryBorderStyle || 'solid';
    const borderColor = section.galleryBorderColor || 'transparent';
    const imagePadding = section.galleryImagePadding || '0px';

    const borderVal = (borderWidth === '0px' || borderStyle === 'none' || borderColor === 'transparent')
        ? 'none'
        : `${borderWidth} ${borderStyle} ${borderColor}`;

    let shadowValue = 'none';
    if (shadow === 'sm') shadowValue = '0 1px 2px 0 rgba(0,0,0,0.05)';
    else if (shadow === 'md') shadowValue = '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)';
    else if (shadow === 'lg') shadowValue = '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)';

    let hoverStyle = '';
    if (hover === 'scale') {
        hoverStyle = 'transform: scale(1.05);';
    } else if (hover === 'opacity') {
        hoverStyle = 'opacity: 0.8;';
    } else if (hover === 'grayscale') {
        hoverStyle = 'filter: grayscale(0);';
    }

    // Dynamic section container width & side margin overrides
    const isFullWidth = Boolean(
        section.galleryFullWidth ||
        section.fullWidth ||
        section.containerWidthMode === 'full' ||
        section.widthMode === 'full' ||
        section.containerMaxWidth === '100%' ||
        section.containerMaxWidth === 'full'
    );
    const containerMaxWidthVal = (() => {
        if (isFullWidth) return '100%';
        if (section.containerWidthMode === 'wide') return '1400px';
        if (section.containerWidthMode === 'compact') return '900px';
        if (section.containerMaxWidth) {
            const v = String(section.containerMaxWidth).trim().toLowerCase();
            if (['100%', 'full', 'none'].includes(v)) return '100%';
            return addUnit(section.containerMaxWidth);
        }
        return null;
    })();
    const containerPaddingVal = (section.containerPaddingLeftRight !== undefined && section.containerPaddingLeftRight !== '')
        ? addUnit(section.containerPaddingLeftRight)
        : ((section.galleryPaddingX !== undefined && section.galleryPaddingX !== '') ? addUnit(section.galleryPaddingX) : null);

    let containerStyleRules = '';
    if (containerMaxWidthVal || containerPaddingVal !== null) {
        const cSelectors = [
            secId ? `.psec-container-${secId}` : null,
            secId ? `[data-gallery-section="${secId}"].container` : null,
            secId ? `[id="${secId}"] .container` : null,
            customId ? `[id="${customId}"] .container` : null,
        ].filter(Boolean).join(',\n');

        if (cSelectors) {
            containerStyleRules = `
                ${cSelectors} {
                    ${containerMaxWidthVal ? `max-width: ${containerMaxWidthVal} !important; width: 100% !important;` : ''}
                    ${containerPaddingVal !== null ? `padding-left: ${containerPaddingVal} !important; padding-right: ${containerPaddingVal} !important;` : ''}
                }
            `;
        }
    }

    const isGridMode = section.galleryLayoutMode === 'grid';
    const isMasonry = ar === 'auto' && !isGridMode;
    let aspectRatioPercentage = '75%'; // 4:3 default
    if (ar === '1/1') aspectRatioPercentage = '100%';
    else if (ar === '16/9') aspectRatioPercentage = '56.25%';
    else if (ar === '2/3') aspectRatioPercentage = '150%';
    else if (ar === '4/3') aspectRatioPercentage = '75%';

    if (isMasonry) {
        return `
            ${containerStyleRules}
            ${wrapperSelectors} {
                column-count: 1;
                column-gap: ${gap};
                display: block;
                padding: 10px 0;
                width: 100%;
                max-width: 100%;
                box-sizing: border-box;
            }
            @media (min-width: 576px) {
                ${wrapperSelectors} {
                    column-count: ${Math.min(2, columns)};
                }
            }
            @media (min-width: 768px) {
                ${wrapperSelectors} {
                    column-count: ${Math.min(3, columns)};
                }
            }
            @media (min-width: 992px) {
                ${wrapperSelectors} {
                    column-count: ${columns};
                }
            }
            ${itemSelectors} {
                display: inline-block !important;
                vertical-align: top;
                width: 100% !important;
                max-width: 100% !important;
                min-width: 0;
                box-sizing: border-box !important;
                margin: 0 0 ${gap} 0 !important;
                margin-bottom: ${gap} !important;
                -webkit-column-break-inside: avoid !important;
                page-break-inside: avoid !important;
                break-inside: avoid !important;
                border-radius: ${radius};
                overflow: hidden;
                box-shadow: ${shadowValue};
                background: #fff;
                transition: all 0.3s ease;
            }
            ${imgWrapSelectors} {
                position: relative;
                width: 100%;
                max-width: 100%;
                box-sizing: border-box;
                padding-top: 0;
                height: auto;
                overflow: hidden;
            }
            ${imgSelectors} {
                position: relative;
                top: auto;
                left: auto;
                width: 100%;
                max-width: 100%;
                height: auto;
                display: block;
                object-fit: contain;
                transition: all 0.3s ease;
                box-sizing: border-box;
                border: ${borderVal};
                padding: ${imagePadding};
                background-color: ${imagePadding !== '0px' ? '#ffffff' : 'transparent'};
                border-radius: ${radius};
                ${hover === 'grayscale' ? 'filter: grayscale(100%);' : ''}
                image-rendering: auto;
                image-rendering: high-quality;
                transform: translateZ(0);
                backface-visibility: hidden;
                -webkit-backface-visibility: hidden;
            }
            ${hoverImgSelectors} {
                ${hoverStyle}
            }
            ${captionOverlaySelectors} {
                position: absolute;
                bottom: 0;
                left: 0;
                right: 0;
                background: linear-gradient(transparent, rgba(0,0,0,0.8));
                padding: 15px;
                color: #fff;
                opacity: 0;
                transition: opacity 0.3s ease;
            }
            ${hoverCaptionOverlaySelectors} {
                opacity: 1;
            }
            ${captionBelowSelectors} {
                padding: 12px;
                background: #fff;
            }
        `;
    }

    if (ar === 'auto') {
        return `
            ${containerStyleRules}
            ${wrapperSelectors} {
                display: grid;
                grid-template-columns: 1fr;
                gap: ${gap};
                align-items: start;
                padding: 10px 0;
                width: 100%;
                max-width: 100%;
                box-sizing: border-box;
            }
            @media (min-width: 576px) {
                ${wrapperSelectors} {
                    grid-template-columns: repeat(auto-fill, minmax(min(100%, 250px), 1fr));
                }
            }
            @media (min-width: 768px) {
                ${wrapperSelectors} {
                    grid-template-columns: repeat(${columns}, minmax(0, 1fr));
                }
            }
            ${itemSelectors} {
                position: relative;
                border-radius: ${radius};
                overflow: hidden;
                box-shadow: ${shadowValue};
                background: #fff;
                transition: all 0.3s ease;
                height: auto;
                min-width: 0;
                max-width: 100%;
                box-sizing: border-box;
            }
            ${imgWrapSelectors} {
                position: relative;
                width: 100%;
                max-width: 100%;
                box-sizing: border-box;
                padding-top: 0;
                height: auto;
                overflow: hidden;
            }
            ${imgSelectors} {
                position: relative;
                top: auto;
                left: auto;
                width: 100%;
                max-width: 100%;
                height: auto;
                display: block;
                object-fit: contain;
                transition: all 0.3s ease;
                box-sizing: border-box;
                border: ${borderVal};
                padding: ${imagePadding};
                background-color: ${imagePadding !== '0px' ? '#ffffff' : 'transparent'};
                border-radius: ${radius};
                ${hover === 'grayscale' ? 'filter: grayscale(100%);' : ''}
                image-rendering: auto;
                image-rendering: high-quality;
                transform: translateZ(0);
                backface-visibility: hidden;
                -webkit-backface-visibility: hidden;
            }
            ${hoverImgSelectors} {
                ${hoverStyle}
            }
            ${captionOverlaySelectors} {
                position: absolute;
                bottom: 0;
                left: 0;
                right: 0;
                background: linear-gradient(transparent, rgba(0,0,0,0.8));
                padding: 15px;
                color: #fff;
                opacity: 0;
                transition: opacity 0.3s ease;
            }
            ${hoverCaptionOverlaySelectors} {
                opacity: 1;
            }
            ${captionBelowSelectors} {
                padding: 12px;
                background: #fff;
            }
        `;
    }

    return `
        ${containerStyleRules}
        ${wrapperSelectors} {
            display: grid;
            grid-template-columns: 1fr;
            gap: ${gap};
            padding: 10px 0;
            width: 100%;
            max-width: 100%;
            box-sizing: border-box;
        }
        @media (min-width: 576px) {
            ${wrapperSelectors} {
                grid-template-columns: repeat(auto-fill, minmax(min(100%, 250px), 1fr));
            }
        }
        @media (min-width: 768px) {
            ${wrapperSelectors} {
                grid-template-columns: repeat(${columns}, minmax(0, 1fr));
            }
        }
        ${itemSelectors} {
            position: relative;
            border-radius: ${radius};
            overflow: hidden;
            box-shadow: ${shadowValue};
            background: #fff;
            transition: all 0.3s ease;
            height: 100%;
            min-width: 0;
            max-width: 100%;
            box-sizing: border-box;
        }
        ${imgWrapSelectors} {
            position: relative;
            width: 100%;
            max-width: 100%;
            box-sizing: border-box;
            padding-top: ${aspectRatioPercentage};
            overflow: hidden;
        }
        ${imgSelectors} {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            max-width: 100%;
            height: 100%;
            object-fit: cover;
            transition: all 0.3s ease;
            box-sizing: border-box;
            border: ${borderVal};
            padding: ${imagePadding};
            background-color: ${imagePadding !== '0px' ? '#ffffff' : 'transparent'};
            border-radius: ${radius};
            ${hover === 'grayscale' ? 'filter: grayscale(100%);' : ''}
            image-rendering: auto;
            image-rendering: high-quality;
            transform: translateZ(0);
            backface-visibility: hidden;
            -webkit-backface-visibility: hidden;
        }
        ${hoverImgSelectors} {
            ${hoverStyle}
        }
        ${captionOverlaySelectors} {
            position: absolute;
            bottom: 0;
            left: 0;
            right: 0;
            background: linear-gradient(transparent, rgba(0,0,0,0.8));
            padding: 15px;
            color: #fff;
            opacity: 0;
            transition: opacity 0.3s ease;
        }
        ${hoverCaptionOverlaySelectors} {
            opacity: 1;
        }
        ${captionBelowSelectors} {
            padding: 12px;
            background: #fff;
        }
    `;
};

// ─── COLUMN IMAGE STYLES GENERATOR ──────────────────────────────────────────────
export const getColImageStyles = (c, imgStyleBase, settings) => {
    if (!c) return '';
    const colId = c.stableId || c.id;
    const rawCustomId = c.customId || c.htmlId || c.elementId || c.colCustomId || c.anchorId;
    const cleanCustomId = rawCustomId ? String(rawCustomId).trim().replace(/^#/, '') : null;
    if (!colId && colId !== 0 && !cleanCustomId) return '';

    const baseId = colId ? (String(colId).startsWith('col-') ? colId : `col-${colId}`) : null;
    const ids = cleanCustomId && baseId && cleanCustomId !== baseId ? [baseId, cleanCustomId] : [cleanCustomId || baseId];
    const targetSelectors = ids.map(i => `#${i} img, #${i} video, #${i} [data-editable="image"]`).join(', ');

    const globalSettings = settings || {};
    const globalBorderWidth = globalSettings.imageBorderWidth || '0px';
    const globalBorderColor = globalSettings.imageBorderColor || '#cbd5e1';
    const globalBorderStyle = globalSettings.imageBorderStyle || 'solid';

    const radius = c.imageBorderRadius !== undefined && c.imageBorderRadius !== '' ? addUnit(c.imageBorderRadius) : (imgStyleBase?.borderRadius || '0px');
    const padding = c.imagePadding !== undefined && c.imagePadding !== '' ? addUnit(c.imagePadding) : (imgStyleBase?.padding || '0px');

    // Resolve per-column overrides, falling back to global settings
    const width = c.imageBorderWidth !== undefined && c.imageBorderWidth !== '' ? addUnit(c.imageBorderWidth) : globalBorderWidth;
    const style = c.imageBorderStyle !== undefined && c.imageBorderStyle !== '' ? c.imageBorderStyle : globalBorderStyle;
    const color = c.imageBorderColor !== undefined && c.imageBorderColor !== '' ? c.imageBorderColor : globalBorderColor;

    // Resolve Image Focus Position & Object Fit
    let objectPos = c.imageObjectPosition || c.imageFocusPosition || c.imagePosition || c.imageFocus || 'center center';
    if (objectPos === 'custom' && c.imageCustomObjectPosition) {
        objectPos = c.imageCustomObjectPosition;
    }
    const objectFit = c.imageObjectFit || c.imageFit || 'cover';
    const heightVal = c.imageHeight ? addUnit(c.imageHeight) : undefined;

    // Only suppress when width is zero or style explicitly 'none'
    const borderVal = (width === '0px' || width === '0' || style === 'none')
        ? 'none'
        : `${width} ${style} ${color}`;

    let shadowValue = imgStyleBase?.boxShadow || 'none';
    if (c.imageShadow !== undefined && c.imageShadow !== '') {
        if (c.imageShadow === 'none') {
            shadowValue = 'none';
        } else if (c.imageShadow === 'sm') {
            shadowValue = '0 1px 2px 0 rgba(0,0,0,0.05)';
        } else if (c.imageShadow === 'md') {
            shadowValue = '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)';
        } else if (c.imageShadow === 'lg') {
            shadowValue = '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)';
        } else if (c.imageShadow === 'xl') {
            shadowValue = '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)';
        } else if (c.imageShadow === 'custom') {
            shadowValue = c.imageCustomShadow || 'none';
        }
    }

    const colFilter = resolveImageFilter((c.imageDesaturate !== undefined || c.imageFilter !== undefined || c.imageCustomFilter !== undefined) ? c : globalSettings);
    const hoverFilter = c.imageHoverFilter || globalSettings.imageHoverFilter;
    const clickMode = c.imageClickInteraction || globalSettings.imageClickInteraction;
    const duration = c.imageTransitionDuration || globalSettings.imageTransitionDuration || '0.4s';
    const timing = c.imageTransitionTiming || globalSettings.imageTransitionTiming || 'cubic-bezier(0.4, 0, 0.2, 1)';

    const hoverStyles = (hoverFilter && hoverFilter !== 'none') ? `
        ${targetSelectors.split(', ').map(s => `${s}:hover`).join(', ')} {
            ${hoverFilter === 'color-on-hover' ? 'filter: none !important; -webkit-filter: none !important;' :
              hoverFilter === 'zoom-color-on-hover' ? 'filter: none !important; -webkit-filter: none !important; transform: scale(1.05) translateZ(0) !important;' :
              hoverFilter === 'desaturate-on-hover' ? 'filter: grayscale(100%) !important; -webkit-filter: grayscale(100%) !important;' :
              `filter: ${hoverFilter} !important; -webkit-filter: ${hoverFilter} !important;`}
        }
    ` : '';

    const clickStyles = (clickMode && clickMode !== 'none') ? `
        ${targetSelectors.split(', ').map(s => `${s}:active`).join(', ')} {
            ${clickMode === 'color-on-click' || clickMode === 'toggle' ? 'filter: none !important; -webkit-filter: none !important; transform: scale(0.98) translateZ(0) !important;' :
              clickMode === 'active-press' ? 'transform: scale(0.97) translateZ(0) !important;' : ''}
        }
    ` : '';

    const bgVal = c.imageBg || c.imageBackgroundColor || globalSettings.imageBg || globalSettings.imageBackgroundColor || (padding && padding !== '0px' ? 'white' : 'transparent');

    return `
        ${targetSelectors} {
            border: ${borderVal} !important;
            padding: ${padding} !important;
            border-radius: ${radius} !important;
            background-color: ${bgVal} !important;
            box-shadow: ${shadowValue} !important;
            box-sizing: border-box !important;
            object-fit: ${objectFit} !important;
            object-position: ${objectPos} !important;
            ${heightVal ? `height: ${heightVal} !important;` : ''}
            ${colFilter && colFilter !== 'none' ? `filter: ${colFilter} !important; -webkit-filter: ${colFilter} !important;` : ''}
            transition: filter ${duration} ${timing}, -webkit-filter ${duration} ${timing}, transform ${duration} ${timing}, opacity ${duration} ${timing}, box-shadow ${duration} ${timing} !important;
            image-rendering: auto !important;
            image-rendering: high-quality !important;
            transform: translateZ(0);
            backface-visibility: hidden !important;
            -webkit-backface-visibility: hidden !important;
        }
        ${hoverStyles}
        ${clickStyles}
    `;
};

// ─── COLUMN MOBILE TEXT STYLES GENERATOR ──────────────────────────────────────
/**
 * Generates a scoped `@media (max-width: 768px)` CSS block for a single column.
 *
 * Priority (highest wins):
 *   1. c.mobileTextScale === 'off'  → skip; no mobile overrides for this column
 *   2. c.mobileHeadingSize / c.mobileBodySize  → explicit per-column mobile sizes
 *   3. settings.mobileTextScale (0–1 float, default 0.78) → auto-scale desktop sizes
 *   4. settings.mobileTextScale === 'off' → global auto-scaling disabled (still
 *      respects explicit per-column sizes if set)
 *
 * @param {object} c           — column data object (stableId, headingSize, textSize, …)
 * @param {object} typography  — site typography config { h2: { size }, body: { size }, … }
 * @param {object} settings    — site settings (mobileTextScale, …)
 * @returns {string} CSS string (may be empty if nothing to override)
 */
export const getColTextStyles = (c, typography, settings) => {
    const colId = c?.stableId || c?.id;
    const rawCustomId = c?.customId || c?.htmlId || c?.elementId || c?.colCustomId || c?.anchorId;
    const cleanCustomId = rawCustomId ? String(rawCustomId).trim().replace(/^#/, '') : null;
    if (!colId && colId !== 0 && !cleanCustomId) return '';

    const globalScale = settings?.mobileTextScale;
    const autoScaleEnabled = globalScale !== 'off' && globalScale !== undefined && globalScale !== '';
    const scaleFactor = autoScaleEnabled ? (parseFloat(globalScale) || 0.78) : null;

    // Helper: parse a raw size string like "2rem", "32px", "2" → { value, unit }
    const parseSize = (raw) => {
        if (!raw) return null;
        const str = String(raw).trim().toLowerCase();
        if (str === '0' || str === '0px' || str === '0rem') return null;
        const match = str.match(/^([\d.]+)(px|rem|em|vw|%)?$/);
        if (!match) return null;
        const val = parseFloat(match[1]);
        // Raw number: if >8 treat as px, else as rem (matches getFontStyles logic)
        let unit = match[2] || (val > 8 ? 'px' : 'rem');
        return { value: val, unit };
    };

    // Convert any size to rem for consistent scaling
    const toRem = (parsed) => {
        if (!parsed) return null;
        if (parsed.unit === 'rem' || parsed.unit === 'em') return parsed.value;
        if (parsed.unit === 'px') return parsed.value / 16;
        return null; // vw/% — skip clamp, keep as-is
    };

    // Resolve desktop sizes for each typography tier
    const desktopTitleRaw = typography?.title?.size || typography?.h1?.size || '2.5rem';
    const desktopH1Raw = c.headingSize || typography?.h1?.size || '2rem';
    const desktopH2Raw = c.headingSize || typography?.h2?.size || '1.75rem';
    const desktopH3Raw = typography?.h3?.size || '1.4rem';
    const desktopBodyRaw = c.textSize || typography?.body?.size || '1.05rem';

    // Compute the mobile font-size: Explicit user override takes priority over auto-scale
    const resolveMobileSize = (desktopRaw, explicitOverride) => {
        // 1. Explicit column Mobile Text override takes priority when provided by user
        if (explicitOverride && String(explicitOverride).trim() !== '') {
            const p = parseSize(explicitOverride);
            if (p) return p.unit === 'px' ? `${p.value}px` : `${p.value}${p.unit}`;
            return addUnit(explicitOverride);
        }

        // 2. Global Auto-Scale applies when active and column hasn't opted out
        if (autoScaleEnabled && c.mobileTextScale !== 'off') {
            const p = parseSize(desktopRaw);
            const remVal = toRem(p);
            if (remVal !== null) {
                const mobileRem = +(remVal * scaleFactor).toFixed(3);
                return `${mobileRem}rem`;
            }
        }

        return null;
    };

    // Compute mobile font-sizes for all 5 tiers
    const mobileTitle = resolveMobileSize(desktopTitleRaw, c.mobileTitleSize || c.mobileHeadingSize);
    const mobileH1 = resolveMobileSize(desktopH1Raw, c.mobileH1Size || c.mobileHeadingSize);
    const mobileH2 = resolveMobileSize(desktopH2Raw, c.mobileH2Size || c.mobileHeadingSize);
    const mobileH3 = resolveMobileSize(desktopH3Raw, c.mobileH3Size || c.mobileHeadingSize);
    const mobileBody = resolveMobileSize(desktopBodyRaw, c.mobileBodySize);

    const titleAlign = c.mobileTitleAlign || c.mobileHeadingAlign;
    const h1Align = c.mobileH1Align || c.mobileHeadingAlign;
    const h2Align = c.mobileH2Align || c.mobileHeadingAlign;
    const h3Align = c.mobileH3Align || c.mobileHeadingAlign;
    const bodyAlign = c.mobileBodyAlign || c.mobileTextAlign;

    const hasMobileStyles = mobileTitle || mobileH1 || mobileH2 || mobileH3 || mobileBody ||
        titleAlign || h1Align || h2Align || h3Align || bodyAlign ||
        c.mobilePadding || c.mobileMargin;
    if (!hasMobileStyles) return '';

    const baseId = colId ? (String(colId).startsWith('col-') ? colId : `col-${colId}`) : null;
    const ids = cleanCustomId && baseId && cleanCustomId !== baseId ? [baseId, cleanCustomId] : [cleanCustomId || baseId];
    const qualify = (selectors) => ids.flatMap(i => selectors.map(s => `#${i} ${s}`.trim())).join(',\n        ');

    const rules = [];

    // Title Rules
    if (mobileTitle || titleAlign) {
        rules.push(`
        ${qualify(['.section-title', '.hero-title', '[data-editable="title"]'])} {
            ${mobileTitle ? `font-size: ${mobileTitle} !important;` : ''}
            ${titleAlign ? `text-align: ${titleAlign} !important;` : ''}
            overflow-wrap: break-word !important;
            word-break: break-word !important;
        }
        ${qualify(['.section-title *', '.hero-title *', '[data-editable="title"] *'])} {
            ${mobileTitle ? `font-size: ${mobileTitle} !important;` : ''}
        }`);
    }

    // H1 Rules
    if (mobileH1 || h1Align) {
        rules.push(`
        ${qualify(['h1', '.h1', '[data-editable="h1"]'])} {
            ${mobileH1 ? `font-size: ${mobileH1} !important;` : ''}
            ${h1Align ? `text-align: ${h1Align} !important;` : ''}
            overflow-wrap: break-word !important;
            word-break: break-word !important;
        }
        ${qualify(['h1 *', '.h1 *', '[data-editable="h1"] *'])} {
            ${mobileH1 ? `font-size: ${mobileH1} !important;` : ''}
        }`);
    }

    // H2 Rules
    if (mobileH2 || h2Align) {
        rules.push(`
        ${qualify(['h2', '.h2', '[data-editable="h2"]', '[data-editable="heading"]'])} {
            ${mobileH2 ? `font-size: ${mobileH2} !important;` : ''}
            ${h2Align ? `text-align: ${h2Align} !important;` : ''}
            overflow-wrap: break-word !important;
            word-break: break-word !important;
        }
        ${qualify(['h2 *', '.h2 *', '[data-editable="h2"] *', '[data-editable="heading"] *'])} {
            ${mobileH2 ? `font-size: ${mobileH2} !important;` : ''}
        }`);
    }

    // H3 Rules
    if (mobileH3 || h3Align) {
        rules.push(`
        ${qualify(['h3', '.h3', '[data-editable="h3"]', '[data-editable="text"]'])} {
            ${mobileH3 ? `font-size: ${mobileH3} !important;` : ''}
            ${h3Align ? `text-align: ${h3Align} !important;` : ''}
            overflow-wrap: break-word !important;
            word-break: break-word !important;
        }
        ${qualify(['h3 *', '.h3 *', '[data-editable="h3"] *', '[data-editable="text"] *'])} {
            ${mobileH3 ? `font-size: ${mobileH3} !important;` : ''}
        }`);
    }

    // Body Rules
    if (mobileBody || bodyAlign) {
        rules.push(`
        ${qualify(['p', 'li', '.body-text', '[data-editable="body"]', '[data-editable="richtext"]'])} {
            ${mobileBody ? `font-size: ${mobileBody} !important;` : ''}
            ${bodyAlign ? `text-align: ${bodyAlign} !important;` : ''}
            overflow-wrap: break-word !important;
            word-break: break-word !important;
        }
        ${qualify(['p *', 'li *', '.body-text *', '[data-editable="body"] *', '[data-editable="richtext"] *'])} {
            ${mobileBody ? `font-size: ${mobileBody} !important;` : ''}
        }`);
    }

    if (c.mobilePadding || c.mobileMargin) {
        const colClassSelectors = ids.map(i => `#${i}.sortable-column`).join(', ');
        rules.push(`
        ${colClassSelectors} {
            ${c.mobilePadding ? `padding: ${addUnit(c.mobilePadding)} !important;` : ''}
            ${c.mobileMargin ? `margin: ${addUnit(c.mobileMargin)} !important;` : ''}
        }`);
    }

    return `@media (max-width: 768px) {${rules.join('')}\n    }`;
};

export const getAppBase = () => {
    if (typeof window === 'undefined') return '';

    // Check if hostname is a subdomain tenant e.g. mybrand.omnivaso.com or omnivaso.mybrand.com
    const host = window.location.hostname || '';
    const hostParts = host.split('.');
    const reservedHostnames = [
        'www', 'api', 'admin', 'shop', 'mail', 'demo', 'test', 'dev', 'ftp', 'app',
        'assets', 'public', 'system', 'root', 'login', 'auth', 'paypal', 'stripe',
        'apple', 'google', 'bank', 'support', 'secure', 'verify', 'billing', 'account', 'checkout', 'localhost', 'omnivaso'
    ];

    const isSubdomainTenant = (hostParts.length >= 3 && hostParts.some(part => !reservedHostnames.includes(part.toLowerCase()) && /^[a-z0-9_-]+$/i.test(part))) ||
        (hostParts.length === 2 && hostParts[1].toLowerCase() === 'localhost' && !reservedHostnames.includes(hostParts[0].toLowerCase()));

    if (isSubdomainTenant) {
        return '';
    }

    const path = window.location.pathname;
    const cleanPath = path.endsWith('/') && path.length > 1 ? path.slice(0, -1) : path;
    if (!cleanPath || cleanPath === '/') return '';

    // 1. Check if cleanPath starts with clean path tenant route e.g. /site/mybrand, /s/mybrand, /sites/mybrand
    const tenantMatch = cleanPath.match(/^(\/(?:sites|s|site)\/[a-z0-9_-]+)/i);
    if (tenantMatch) {
        return tenantMatch[1];
    }

    return '';
};

export const getTenantHandle = () => {
    if (typeof window === 'undefined') return '';
    const reserved = [
        'www', 'api', 'admin', 'shop', 'mail', 'demo', 'test', 'dev', 'ftp', 'app',
        'assets', 'public', 'system', 'root', 'login', 'auth', 'paypal', 'stripe',
        'apple', 'google', 'bank', 'support', 'secure', 'verify', 'billing', 'account', 'checkout', 'localhost', 'omnivaso',
        'new', 'stage', 'staging', 'beta', 'preview', 'prod', 'production', 'store', 'cdn', 'static'
    ];

    const host = (window.location.hostname || '').toLowerCase().replace(/^www\./i, '');

    // 1. Check Subdomains on platform hosts (e.g. mybrand.omnivaso.com or mybrand.localhost)
    if (host && host !== 'omnivaso.com' && host !== 'localhost' && host !== '127.0.0.1') {
        const isPlatformHost = host.includes('omnivaso.com') || host.includes('localhost');
        if (isPlatformHost) {
            const hostParts = host.split('.');
            for (const part of hostParts) {
                const lower = part.toLowerCase();
                if (!reserved.includes(lower) && /^[a-z0-9_-]+$/i.test(lower)) {
                    return lower;
                }
            }
        }
    }

    // 2. Check path base (e.g. /mybrand or /sites/mybrand)
    const base = getAppBase();
    if (base && base.startsWith('/')) {
        const segments = base.replace(/^\/+/, '').split('/').filter(Boolean);
        const lastSeg = segments[segments.length - 1];
        if (lastSeg && !reserved.includes(lastSeg.toLowerCase())) {
            return lastSeg.toLowerCase();
        }
    }

    // 3. Check query param fallback (?site=mybrand or ?tenant=mybrand)
    try {
        const params = new URLSearchParams(window.location.search);
        const qSite = params.get('site') || params.get('tenant') || params.get('subdomain');
        if (qSite && !reserved.includes(qSite.toLowerCase())) {
            return qSite.toLowerCase();
        }
    } catch (e) { }

    return '';
};

export const getTenantStorageKey = (key) => {
    const handle = getTenantHandle();
    const prefix = handle ? handle.replace(/[^a-z0-9-]/gi, '_') : 'root';
    return `omnivaso_${prefix}_${key}`;
};

export const authHeaders = (customConfig = {}) => {
    let token = null;
    if (typeof localStorage !== 'undefined') {
        const tenantKey = getTenantStorageKey('adminToken');
        token = localStorage.getItem(tenantKey) || localStorage.getItem('adminToken');
        if (!token && typeof sessionStorage !== 'undefined') {
            token = sessionStorage.getItem(tenantKey) || sessionStorage.getItem('adminToken');
        }
        if (!token && localStorage.getItem('omnivaso_demo_mode') === 'true') {
            token = 'demo-token';
        }
    }
    const tenant = getTenantHandle();

    let baseHeaders = {};
    if (customConfig && customConfig.headers) {
        baseHeaders = { ...customConfig.headers };
    } else if (customConfig && typeof customConfig === 'object') {
        baseHeaders = { ...customConfig };
    }

    if (token) {
        baseHeaders['Authorization'] = `Bearer ${token}`;
        baseHeaders['X-Admin-Token'] = token;
    }
    if (tenant) {
        baseHeaders['X-Tenant'] = tenant;
    }

    const res = { ...customConfig, headers: baseHeaders };
    if (token) {
        res['Authorization'] = `Bearer ${token}`;
        res['X-Admin-Token'] = token;
    }
    if (tenant) {
        res['X-Tenant'] = tenant;
    }
    return res;
};

export const getApiUrl = (endpoint = 'api.php') => {
    const tenant = getTenantHandle();
    const cleanEndpoint = endpoint.replace(/^\/+/, '');
    const hasQuery = cleanEndpoint.includes('?');
    const sep = hasQuery ? '&' : '?';

    const params = [];
    if (tenant) {
        params.push(`site=${encodeURIComponent(tenant)}`);
    }

    // Attach token fallback if available to bypass environments where Apache/FastCGI strips Authorization headers
    if (typeof localStorage !== 'undefined') {
        const tenantKey = getTenantStorageKey('adminToken');
        const token = localStorage.getItem(tenantKey) || localStorage.getItem('adminToken') || (typeof sessionStorage !== 'undefined' ? (sessionStorage.getItem(tenantKey) || sessionStorage.getItem('adminToken')) : null);
        if (token && !cleanEndpoint.includes('token=')) {
            params.push(`token=${encodeURIComponent(token)}`);
        }
    }

    if (params.length > 0) {
        return `/${cleanEndpoint}${sep}${params.join('&')}`;
    }
    return `/${cleanEndpoint}`;
};

/**
 * safePost — wraps axios.post with Base64-encoded payload to bypass ModSecurity WAF
 * pattern matching on content like SQL fragments, PHP function names, etc.
 * The server's getDecodedInput() / $inputBody handling will transparently decode it.
 *
 * Usage: safePost(url, data, axiosConfig)
 *        Drop-in replacement for: axios.post(url, data, config)
 */
export const safePost = async (url, data, config = {}) => {
    const axios = (await import('axios')).default;
    try {
        const json = typeof data === 'string' ? data : JSON.stringify(data);
        const b64 = btoa(unescape(encodeURIComponent(json)));
        const wrappedConfig = {
            ...config,
            headers: {
                ...(config.headers || {}),
                'Content-Type': 'application/json',
            }
        };
        return await axios.post(url, { payload_b64: b64 }, wrappedConfig);
    } catch (err) {
        // Fallback to raw post on encode failure
        const axios2 = (await import('axios')).default;
        return axios2.post(url, data, config);
    }
};

export const getPlatformApiUrl = (endpoint = 'api_ecommerce.php?action=platform-payment-public') => {
    const cleanEndpoint = endpoint.replace(/^\/+/, '');
    if (typeof window !== 'undefined') {
        const host = window.location.hostname;
        const isOmnivasoDomain = host === 'omnivaso.com' || host.endsWith('.omnivaso.com');
        if (!isOmnivasoDomain && host !== 'localhost' && host !== '127.0.0.1') {
            return `https://omnivaso.com/${cleanEndpoint}`;
        }
    }
    return getApiUrl(cleanEndpoint);
};

export const resolveMediaUrl = (url) => {
    if (!url || typeof url !== 'string') return '';
    // Strip leading/trailing whitespace and quotes
    let trimmed = url.trim().replace(/^['"]+|['"]+$/g, '').trim();
    if (!trimmed) return '';

    if (trimmed.startsWith('data:') || trimmed.startsWith('blob:')) {
        return trimmed;
    }

    // Upgrade http:// to https:// to prevent mixed-content blocking
    if (trimmed.startsWith('http://')) {
        trimmed = 'https://' + trimmed.substring(7);
    }

    const origin = (typeof window !== 'undefined' && window.location) ? window.location.origin : '';
    const hostname = (typeof window !== 'undefined' && window.location) ? window.location.hostname : '';
    if (origin && trimmed.startsWith(origin)) {
        trimmed = trimmed.substring(origin.length);
    }

    // If full URL points to an internal host, strip domain
    if (/^https?:\/\//i.test(trimmed)) {
        try {
            const parsed = new URL(trimmed);
            const isInternalHost = (
                (origin && trimmed.startsWith(origin)) ||
                (hostname && parsed.hostname === hostname) ||
                /(?:^|\.)(?:omnivaso\.com|localhost)$/i.test(parsed.hostname) ||
                /\/(?:sites|s|site)\//i.test(parsed.pathname)
            );
            if (isInternalHost) {
                trimmed = parsed.pathname + parsed.search + parsed.hash;
            } else {
                return trimmed;
            }
        } catch (e) {
            trimmed = trimmed.replace(/^https?:\/\/[^\/]+/i, '');
        }
    }

    let cleaned = trimmed;
    if (cleaned.startsWith('./')) cleaned = cleaned.substring(2);
    if (cleaned.startsWith('public/')) cleaned = cleaned.substring(7);
    if (cleaned.startsWith('/public/')) cleaned = cleaned.substring(8);

    const rootAssets = ['hero_abstract_bg.png', 'apple-touch-icon.png', 'favicon.ico', 'favicon.png', 'favicon.svg', 'vite.svg'];
    const bareName = cleaned.replace(/^\/+/, '');
    if (rootAssets.includes(bareName)) {
        return '/' + bareName;
    }

    cleaned = cleaned.replace(/\/+/g, '/');
    if (!cleaned.startsWith('/')) {
        cleaned = '/' + cleaned;
    }

    // Strip true duplicate tenant path prefixes (e.g. /s/foo/s/foo/uploads/... -> /sites/foo/uploads/...)
    cleaned = cleaned.replace(/^\/(?:sites|s|site)\/([a-z0-9_-]+)\/(?:sites|s|site)\/\1\//i, '/sites/$1/');

    const base = getAppBase();
    const tenant = getTenantHandle();

    // 1. Check if the URL points to a tenant uploads directory: /sites/:tenant/uploads/... or /s/:tenant/uploads/... or /site/:tenant/uploads/...
    const sitesTenantMatch = cleaned.match(/^\/(?:sites|s|site)\/([^\/]+)\/uploads\/(.*)$/i);
    if (sitesTenantMatch) {
        const urlTenant = sitesTenantMatch[1].toLowerCase();
        const uploadFile = sitesTenantMatch[2];

        // Standalone / custom domain: If explicitly on a standalone custom domain (not an omnivaso.com platform host)
        const host = (typeof window !== 'undefined' && window.location.hostname) ? window.location.hostname.toLowerCase() : '';
        const isStandaloneCustomDomain = host && !host.endsWith('omnivaso.com') && host !== 'localhost' && host !== '127.0.0.1';
        if (!base && isStandaloneCustomDomain) {
            return `/uploads/${uploadFile}`;
        }

        // Clean path route: If we are on clean path like /s/:tenant and tenant matches
        if (base) {
            const baseTenant = base.replace(/^\/(?:sites|s|site)\//i, '').replace(/^\//, '').toLowerCase();
            if (baseTenant === urlTenant) {
                return `${base}/uploads/${uploadFile}`;
            }
        }

        // On platform hosts, /sites/:tenant/uploads/:file is a physical directory path that always exists directly
        return `/sites/${urlTenant}/uploads/${uploadFile}`;
    }

    // 2. URL points to /uploads/... without tenant prefix
    if (cleaned.startsWith('/uploads/')) {
        const uploadFile = cleaned.substring(9);

        // Standalone / custom domain: serve root /uploads/
        const host = (typeof window !== 'undefined' && window.location.hostname) ? window.location.hostname.toLowerCase() : '';
        const isStandaloneCustomDomain = host && !host.endsWith('omnivaso.com') && host !== 'localhost' && host !== '127.0.0.1';
        if (isStandaloneCustomDomain) {
            return cleaned;
        }

        // If clean path route exists (e.g. /s/mybrand), prepend base
        if (base && !cleaned.startsWith(base)) {
            return `${base}${cleaned}`;
        }

        // If a tenant handle is active (subdomain, ?site= query param, or context), route to tenant's uploads
        if (tenant) {
            return `/sites/${tenant}/uploads/${uploadFile}`;
        }

        return cleaned;
    }

    if (base && !cleaned.startsWith(base)) {
        return base + (cleaned.startsWith('/') ? cleaned : '/' + cleaned);
    }
    return cleaned;
};

export const isFileUrl = (url) => {
    if (!url || typeof url !== 'string') return false;
    const clean = url.trim().split('#')[0].split('?')[0].toLowerCase();
    if (/\.(pdf|doc|docx|xls|xlsx|ppt|pptx|csv|zip|rar|7z|gz|tar|mp3|mp4|mov|avi|wav|png|jpe?g|gif|webp|svg|ico|txt)$/i.test(clean)) return true;
    if (/\/uploads\//i.test(clean)) return true;
    return false;
};

export const isExternalOrFileUrl = (url) => {
    if (!url || typeof url !== 'string') return false;
    const trimmed = url.trim();
    if (/^(?:https?:\/\/|mailto:|tel:|javascript:|blob:|data:)/i.test(trimmed)) return true;
    if (trimmed.startsWith('#')) return false;
    return isFileUrl(trimmed);
};

export const normalizeAppUrl = (url) => {
    if (!url) return '';
    if (typeof url !== 'string') return url;

    let newUrl = url.trim();
    if (!newUrl) return '';

    // Standard external protocols mailto / tel should be returned as-is
    if (newUrl.startsWith('mailto:') || newUrl.startsWith('tel:')) {
        return newUrl;
    }

    // Section anchor links like #creative should remain unchanged
    if (newUrl.startsWith('#')) {
        return newUrl;
    }

    // Direct file downloads / uploads (PDF, DOCX, ZIP, etc. or /uploads/ path)
    // Should NOT be converted to internal React Router SPA routes or stripped of tenant prefixes
    if (isFileUrl(newUrl) || /\/uploads\//i.test(newUrl)) {
        return resolveMediaUrl(newUrl);
    }

    const origin = (typeof window !== 'undefined' && window.location) ? window.location.origin : '';
    const hostname = (typeof window !== 'undefined' && window.location) ? window.location.hostname : '';

    // If full HTTP/HTTPS URL, check if it points to internal app/domain or contains a tenant prefix (/sites/, /s/, /site/)
    if (/^https?:\/\//i.test(newUrl)) {
        try {
            const parsed = new URL(newUrl);
            const isInternalHost = (
                (origin && newUrl.startsWith(origin)) ||
                (hostname && parsed.hostname === hostname) ||
                /(?:^|\.)(?:omnivaso\.com|localhost)$/i.test(parsed.hostname) ||
                /\/(?:sites|s|site)\//i.test(parsed.pathname)
            );
            if (isInternalHost) {
                // Extract relative path + query + hash
                newUrl = parsed.pathname + parsed.search + parsed.hash;
            } else {
                // True external web link (e.g., https://google.com)
                return newUrl;
            }
        } catch (e) {
            if (origin && newUrl.startsWith(origin)) {
                newUrl = newUrl.substring(origin.length);
            }
            newUrl = newUrl.replace(/^https?:\/\/(?:www\.)?(?:omnivaso\.com)/i, '');
        }
    }

    // Convert hash routing remnants (e.g., #/pages/... or /#/pages/... to /pages/...)
    const isHashRoute = /^\/?#\//.test(newUrl);
    if (isHashRoute) {
        newUrl = newUrl.replace(/^\/?#\//, '/');
    }

    // Convert legacy /shop?category=..., shop?category=..., or ?category=... to clean /products/:slug
    const shopCatMatch = newUrl.match(/^\/?(?:shop|products)?\?(?:category|cat)=([^&#]+)/i);
    if (shopCatMatch) {
        const rawCat = decodeURIComponent(shopCatMatch[1]).replace(/\+/g, ' ');
        const subMatch = newUrl.match(/[?&](?:subcategory|subcat)=([^&#]+)/i);
        const slug = rawCat.toLowerCase().trim().replace(/\s+/g, '-').replace(/[^\w-]+/g, '').replace(/--+/g, '-').replace(/^-+/, '').replace(/-+$/, '');
        if (slug) {
            if (subMatch) {
                newUrl = `/products/${slug}?subcategory=${subMatch[1]}`;
            } else {
                newUrl = `/products/${slug}`;
            }
        } else {
            newUrl = '/shop';
        }
    }

    // Strip any and all tenant path prefixes (single or repeated) e.g.:
    // /sites/elkingtoninvestments/sites/elkingtoninvestments/about -> /about
    // sites/elkingtoninvestments/about -> /about
    // /sites/elkingtoninvestments#footer -> /#footer
    newUrl = newUrl.replace(/^(?:\/?(?:sites|s|site)\/[^\/#?]+)+/i, '');

    // Also check current app base and tenant name if present at start
    const base = getAppBase();
    if (base) {
        const escBase = base.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const baseRegex = new RegExp(`^${escBase}(?=\\/|\\?|#|$)`, 'i');
        newUrl = newUrl.replace(baseRegex, '');

        const tenantName = base.replace(/^\/+(?:sites|s|site)\//i, '').replace(/^\/+/, '');
        if (tenantName) {
            const escTenant = tenantName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            const tenantRegex = new RegExp(`^\\/${escTenant}(?=\\/|\\?|#|$)`, 'i');
            newUrl = newUrl.replace(tenantRegex, '');
        }
    }

    // Handle query parameter link targets:
    // 1. '/?demo=true' -> explicitly targets root homepage with demo query
    // 2. '?demo=true' or 'demo=true' -> relative query parameter on current page
    if (/^\/?(?:\?|demo=)/i.test(newUrl)) {
        if (newUrl.startsWith('/?')) {
            newUrl = '/?' + newUrl.substring(2);
        } else if (newUrl.startsWith('?')) {
            newUrl = '?' + newUrl.substring(1);
        } else if (newUrl.toLowerCase().startsWith('demo=')) {
            newUrl = '?' + newUrl;
        }
    }

    // Normalize leading slash for internal relative URLs (except query-only strings starting with '?' or anchors '#')
    if (!newUrl.startsWith('/') && !newUrl.startsWith('?') && !newUrl.startsWith('#')) {
        newUrl = '/' + newUrl;
    }

    if (newUrl === '' || newUrl === '//') {
        newUrl = '/';
    }

    return newUrl;
};

export const getTenantFullUrl = (url) => {
    if (!url || typeof url !== 'string') return '';
    const trimmed = url.trim();
    if (!trimmed) return '';

    if (isFileUrl(trimmed) || /\/uploads\//i.test(trimmed)) {
        return resolveMediaUrl(trimmed);
    }

    // If external protocol or mailto/tel or anchor
    if (/^(?:https?:\/\/|mailto:|tel:|javascript:)/i.test(trimmed)) {
        const origin = (typeof window !== 'undefined' && window.location) ? window.location.origin : '';
        const hostname = (typeof window !== 'undefined' && window.location) ? window.location.hostname : '';
        const isInternalHost = (
            (origin && trimmed.startsWith(origin)) ||
            (hostname && trimmed.includes(hostname)) ||
            /(?:^|\.)(?:omnivaso\.com|localhost)$/i.test(trimmed) ||
            /\/(?:sites|s|site)\//i.test(trimmed)
        );
        if (!isInternalHost) {
            return trimmed;
        }
    }
    if (trimmed.startsWith('#')) {
        return trimmed;
    }

    const norm = normalizeAppUrl(trimmed);
    const base = getAppBase();
    if (base && !norm.startsWith(base)) {
        return `${base}${norm.startsWith('/') ? norm : '/' + norm}`;
    }
    return norm;
};

export const handleAppUrlClick = (e, rawUrl, isEditing = false) => {
    if (isEditing) {
        if (e && e.preventDefault) e.preventDefault();
        return true;
    }
    if (!rawUrl || typeof rawUrl !== 'string') return false;
    const trimmed = rawUrl.trim();
    if (!trimmed.includes('#')) return false;

    const hashIndex = trimmed.indexOf('#');
    const pathPart = trimmed.substring(0, hashIndex).trim();
    const hashPart = trimmed.substring(hashIndex + 1).trim();

    if (!hashPart) return false;

    const origin = (typeof window !== 'undefined' && window.location) ? window.location.origin : '';
    let cleanPath = pathPart;
    if (origin && cleanPath.startsWith(origin)) {
        cleanPath = cleanPath.substring(origin.length);
    }

    const normCleanPath = normalizeAppUrl(cleanPath);
    const currentRawPath = (typeof window !== 'undefined' && window.location) ? window.location.pathname : '';
    const normCurrentPath = normalizeAppUrl(currentRawPath);

    // Check if anchor element exists on current DOM
    const targetElement = typeof document !== 'undefined' ? findAnchorElement(hashPart) : null;
    const isSamePath = normCleanPath === normCurrentPath;

    // Consider current page if:
    // 1. The target element already exists in the DOM (works on any route)
    // 2. The path explicitly matches the current pathname
    // 3. The link is a bare anchor (#id) with no path and we're on the home page
    const isBareAnchor = normCleanPath === '' || normCleanPath === '/';
    const isCurrentPage = Boolean(targetElement) || isSamePath || (isBareAnchor && (normCurrentPath === '/' || normCurrentPath === ''));

    if (isCurrentPage) {
        if (e && e.preventDefault) e.preventDefault();
        if (typeof window !== 'undefined') {
            const currentHash = window.location.hash ? window.location.hash.replace(/^#/, '') : '';
            if (currentHash !== hashPart) {
                window.history.pushState(null, '', `${window.location.pathname}${window.location.search}#${hashPart}`);
                window.dispatchEvent(new Event('hashchange'));
            } else if (hashPart === 'claim' || hashPart === '/claim') {
                window.dispatchEvent(new Event('hashchange'));
            }
        }
        smoothScrollTo(hashPart);
        return true;
    }
    return false;
};

export const formatHtmlDescription = (str) => {
    if (!str || typeof str !== 'string') return '';
    let text = str.trim();
    if (!text) return '';

    // Normalize literal escape sequences (\r\n, \n, \r)
    text = text.replace(/\\r\\n/g, '\n').replace(/\\r/g, '\n').replace(/\\n/g, '\n');

    // Remove CDATA wrappers
    text = text.replace(/<!\[CDATA\[([\s\S]*?)\]\]>/gi, '$1');

    // Check if HTML block tags exist (<p>, <div>, <h4>, <h3>, <ul>, <ol>, <li>, table, blockquote, section, article, br)
    const hasBlockTags = /<(?:p|div|h[1-6]|ul|ol|li|table|blockquote|section|article|br)[^>]*>/i.test(text);

    if (!hasBlockTags) {
        text = text
            .split(/\n\s*\n/)
            .map(para => {
                const trimmed = para.trim();
                return trimmed ? `<p>${trimmed.replace(/\n/g, '<br />')}</p>` : '';
            })
            .filter(Boolean)
            .join('\n');
    }

    return text;
};

export const resolveHtmlMediaUrls = (html) => {
    if (!html) return '';
    if (typeof html !== 'string') return html;
    const formatted = formatHtmlDescription(html);
    const base = getAppBase();

    // 1. Initial quick string replaces for common upload prefixes
    let resolved = formatted.replace(/src=(['"])http:\/\//gi, 'src=$1https://');
    resolved = resolved.replace(/src=(['"])\.\/uploads\//g, `src=$1${base}/uploads/`);
    resolved = resolved.replace(/src=(['"])uploads\//g, `src=$1${base}/uploads/`);
    resolved = resolved.replace(/src=(['"])\.\/sites\//g, `src=$1${base}/sites/`);
    resolved = resolved.replace(/src=(['"])sites\//g, `src=$1${base}/sites/`);

    // 2. Comprehensive DOMParser resolution for media elements & links
    if (typeof DOMParser !== 'undefined') {
        try {
            const parser = new DOMParser();
            const doc = parser.parseFromString(resolved, 'text/html');

            // Resolve Images & Enforce Accessibility alt Attribute & Explicit Layout Dimensions
            const images = doc.querySelectorAll('img');
            images.forEach(img => {
                const src = img.getAttribute('src');
                if (src) {
                    img.setAttribute('src', resolveMediaUrl(src));
                }
                if (!img.hasAttribute('alt')) {
                    img.setAttribute('alt', '');
                }
                if (!img.hasAttribute('loading')) {
                    img.setAttribute('loading', 'lazy');
                }
                if (!img.hasAttribute('decoding')) {
                    img.setAttribute('decoding', 'async');
                }
                if (!img.hasAttribute('width') && !img.hasAttribute('height')) {
                    img.setAttribute('width', '800');
                    img.setAttribute('height', '600');
                }
            });

            // Resolve Videos & Sources
            const videos = doc.querySelectorAll('video, source');
            videos.forEach(vid => {
                const src = vid.getAttribute('src');
                if (src) {
                    vid.setAttribute('src', resolveMediaUrl(src));
                }
                const poster = vid.getAttribute('poster');
                if (poster) {
                    vid.setAttribute('poster', resolveMediaUrl(poster));
                }
            });

            // Resolve Iframes
            const iframes = doc.querySelectorAll('iframe');
            iframes.forEach(iframe => {
                const src = iframe.getAttribute('src');
                if (src && src.startsWith('http://')) {
                    iframe.setAttribute('src', 'https://' + src.substring(7));
                }
            });

            // Resolve Links & Enforce Descriptive Link Accessibility
            const links = doc.querySelectorAll('a');
            links.forEach(link => {
                let href = link.getAttribute('href');
                if (href) {
                    const isFile = isFileUrl(href);
                    const norm = isFile ? resolveMediaUrl(href) : normalizeAppUrl(href);
                    const isExternal = norm.startsWith('http://') || norm.startsWith('https://');
                    const isMailOrTel = norm.startsWith('mailto:') || norm.startsWith('tel:');

                    if (isFile) {
                        link.setAttribute('href', norm);
                        link.setAttribute('target', '_blank');
                        link.setAttribute('rel', 'noopener noreferrer');
                    } else if (!isExternal && !isMailOrTel && !norm.startsWith('#')) {
                        const fullHref = getTenantFullUrl(href);
                        link.setAttribute('href', fullHref);
                        link.removeAttribute('target');
                        link.removeAttribute('rel');
                    } else if (isExternal) {
                        link.setAttribute('href', norm);
                        link.setAttribute('target', '_blank');
                        link.setAttribute('rel', 'noopener noreferrer');
                    }
                }

                // Check for non-descriptive link text or missing aria-label / title for complete accessibility tree compliance
                const linkTextClean = (link.textContent || '').trim().toUpperCase();
                const innerImg = link.querySelector('img');
                const isImageOnlyLink = !linkTextClean || (innerImg && (linkTextClean === (innerImg.getAttribute('alt') || '').trim().toUpperCase()));
                const isGenericLinkText = linkTextClean === 'HERE' || linkTextClean === 'CLICK HERE' || linkTextClean === 'LINK' || linkTextClean === 'READ MORE' || linkTextClean === 'MORE' || linkTextClean === 'LEARN MORE';
                const lacksAccessibleName = !link.getAttribute('aria-label') && !link.getAttribute('title');

                if (isGenericLinkText || isImageOnlyLink || lacksAccessibleName) {
                    const targetHref = href || link.getAttribute('href') || '';
                    const cleanPath = targetHref.replace(/^https?:\/\/[^\/]+/i, '').replace(/^\//, '').replace(/\/$/, '');
                    const domainHost = targetHref.replace(/^https?:\/\//i, '').replace(/^www\./i, '').split('/')[0];
                    const labelName = cleanPath ? cleanPath.replace(/[-_]/g, ' ') : (domainHost || 'page details');
                    const imgAlt = innerImg ? (innerImg.getAttribute('alt') || '').trim() : '';
                    const descriptiveLabel = (imgAlt && imgAlt !== '') ? imgAlt : (linkTextClean && !isGenericLinkText ? linkTextClean : `Visit ${labelName}`);

                    if (!link.getAttribute('aria-label')) {
                        link.setAttribute('aria-label', descriptiveLabel);
                    }
                    if (!link.getAttribute('title')) {
                        link.setAttribute('title', descriptiveLabel);
                    }
                    if (innerImg && (!imgAlt || imgAlt === '')) {
                        innerImg.setAttribute('alt', descriptiveLabel);
                    }
                }
            });
            resolved = doc.body.innerHTML;
        } catch (e) { }
    }
    return resolved;
};

// ─── OVERLAY CSS HELPER ──────────────────────────────────────────────────────
export const resolveOverlayCss = (opts = {}) => {
    const {
        overlayOpacity = 0.4,
        overlayColor = '#000000',
        overlayAlways = false,
        overlayType = 'solid',
        overlayGradientAngle = '180',
        overlayGradientStart,
        overlayGradientMid,
        overlayGradientEnd,
        overlayGradientStartOpacity,
        overlayGradientMidOpacity,
        overlayGradientEndOpacity,
        overlayGradientStartPos,
        overlayGradientMidPos,
        overlayGradientEndPos,
    } = opts;

    const masterDimmer = (overlayOpacity !== undefined && overlayOpacity !== null && !isNaN(parseFloat(overlayOpacity)))
        ? Math.max(0, Math.min(1, parseFloat(overlayOpacity)))
        : 1.0;

    const hexToRgb = (hex) => {
        if (!hex || typeof hex !== 'string') return '0, 0, 0';
        let clean = hex.trim();
        if (clean.startsWith('rgba') || clean.startsWith('rgb')) {
            const match = clean.match(/rgba?\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
            if (match) return `${match[1]}, ${match[2]}, ${match[3]}`;
        }
        if (clean.startsWith('#')) clean = clean.slice(1);
        if (clean.length === 3) clean = clean.split('').map(c => c + c).join('');
        if (clean.length === 8) clean = clean.slice(0, 6);
        if (clean.length !== 6) return '0, 0, 0';
        const r = parseInt(clean.slice(0, 2), 16);
        const g = parseInt(clean.slice(2, 4), 16);
        const b = parseInt(clean.slice(4, 6), 16);
        if (isNaN(r) || isNaN(g) || isNaN(b)) return '0, 0, 0';
        return `${r}, ${g}, ${b}`;
    };

    const formatColorStop = (color, opacity, defaultOpacity, pos) => {
        const rgb = hexToRgb(color);
        const effOpacity = (opacity !== undefined && opacity !== '' && !isNaN(parseFloat(opacity)))
            ? parseFloat(opacity)
            : defaultOpacity;
        const scaledOpacity = effOpacity * masterDimmer;
        const safeOpacity = isNaN(scaledOpacity) ? (defaultOpacity * masterDimmer) : Math.max(0, Math.min(1, scaledOpacity));
        const posSuffix = (pos !== undefined && pos !== '' && !isNaN(parseFloat(pos))) ? ` ${parseFloat(pos)}%` : '';
        return `rgba(${rgb}, ${safeOpacity})${posSuffix}`;
    };

    let background = `rgba(${hexToRgb(overlayColor)}, ${masterDimmer})`;
    let showOverlay = (masterDimmer > 0 || overlayAlways);

    const rawAngle = String(overlayGradientAngle !== undefined ? overlayGradientAngle : '180').replace(/[^0-9.-]/g, '');
    const angle = rawAngle !== '' ? rawAngle : '180';

    if (overlayType === 'gradient') {
        const startColor = overlayGradientStart || overlayColor || '#000000';
        const rawStartOpacity = (overlayGradientStartOpacity !== undefined && overlayGradientStartOpacity !== '' && !isNaN(parseFloat(overlayGradientStartOpacity)))
            ? parseFloat(overlayGradientStartOpacity)
            : 0.8;
        const endColor = overlayGradientEnd || overlayColor || '#000000';
        const rawEndOpacity = (overlayGradientEndOpacity !== undefined && overlayGradientEndOpacity !== '' && !isNaN(parseFloat(overlayGradientEndOpacity)))
            ? parseFloat(overlayGradientEndOpacity)
            : 0;

        const startStop = formatColorStop(startColor, rawStartOpacity, 0.8, overlayGradientStartPos);
        const endStop = formatColorStop(endColor, rawEndOpacity, 0, overlayGradientEndPos);

        if (overlayGradientMid) {
            const rawMidOpacity = (overlayGradientMidOpacity !== undefined && overlayGradientMidOpacity !== '' && !isNaN(parseFloat(overlayGradientMidOpacity)))
                ? parseFloat(overlayGradientMidOpacity)
                : ((rawStartOpacity + rawEndOpacity) / 2);
            const midStop = formatColorStop(overlayGradientMid, rawMidOpacity, 0.4, overlayGradientMidPos !== undefined ? overlayGradientMidPos : 50);
            background = `linear-gradient(${angle}deg, ${startStop}, ${midStop}, ${endStop})`;
        } else {
            background = `linear-gradient(${angle}deg, ${startStop}, ${endStop})`;
        }
        showOverlay = (masterDimmer > 0 && (rawStartOpacity > 0 || rawEndOpacity > 0 || (overlayGradientMid && parseFloat(overlayGradientMidOpacity || 0.4) > 0))) || overlayAlways;
    } else if (overlayType === 'customGradient') {
        const startColor = overlayGradientStart || overlayColor || '#000000';
        const rawStartOpacity = (overlayGradientStartOpacity !== undefined && overlayGradientStartOpacity !== '' && !isNaN(parseFloat(overlayGradientStartOpacity)))
            ? parseFloat(overlayGradientStartOpacity)
            : 0.85;
        const endColor = overlayGradientEnd || '#1e293b';
        const rawEndOpacity = (overlayGradientEndOpacity !== undefined && overlayGradientEndOpacity !== '' && !isNaN(parseFloat(overlayGradientEndOpacity)))
            ? parseFloat(overlayGradientEndOpacity)
            : 0.85;

        const startStop = formatColorStop(startColor, rawStartOpacity, 0.85, overlayGradientStartPos);
        const endStop = formatColorStop(endColor, rawEndOpacity, 0.85, overlayGradientEndPos);

        if (overlayGradientMid) {
            const rawMidOpacity = (overlayGradientMidOpacity !== undefined && overlayGradientMidOpacity !== '' && !isNaN(parseFloat(overlayGradientMidOpacity)))
                ? parseFloat(overlayGradientMidOpacity)
                : 0.7;
            const midStop = formatColorStop(overlayGradientMid, rawMidOpacity, 0.7, overlayGradientMidPos !== undefined ? overlayGradientMidPos : 50);
            background = `linear-gradient(${angle}deg, ${startStop}, ${midStop}, ${endStop})`;
        } else {
            background = `linear-gradient(${angle}deg, ${startStop}, ${endStop})`;
        }
        showOverlay = (masterDimmer > 0 && (rawStartOpacity > 0 || rawEndOpacity > 0 || (overlayGradientMid && parseFloat(overlayGradientMidOpacity || 0.7) > 0))) || overlayAlways;
    }

    return { background, showOverlay };
};

export const findAnchorElement = (rawId) => {
    if (typeof document === 'undefined' || !rawId || typeof rawId !== 'string') return null;
    const cleanId = rawId.trim().replace(/^#/, '');
    if (!cleanId) return null;

    // 1. Direct ID match
    let el = document.getElementById(cleanId);
    if (el) return el;

    // 2. Query selector with CSS escape
    try {
        el = document.querySelector(`#${CSS.escape(cleanId)}`);
        if (el) return el;
    } catch (e) { }

    // 3. Name or data attributes
    el = document.querySelector(`[name="${cleanId}"], [data-anchor="${cleanId}"], [data-custom-id="${cleanId}"], [data-section-id="${cleanId}"], [data-column-id="${cleanId}"]`);
    if (el) return el;

    // 4. Candidate ID variations with prefixes
    const bareId = cleanId.replace(/^(sec|col|section|column)-/i, '');
    const candidates = [
        `sec-${cleanId}`,
        `col-${cleanId}`,
        `section-${cleanId}`,
        `column-${cleanId}`,
        bareId,
        `sec-${bareId}`,
        `col-${bareId}`,
        `section-${bareId}`,
        `column-${bareId}`
    ];

    for (const cand of candidates) {
        if (!cand || cand === cleanId) continue;
        el = document.getElementById(cand);
        if (el) return el;
        try {
            el = document.querySelector(`#${CSS.escape(cand)}`);
            if (el) return el;
        } catch (e) { }
    }

    // 5. Case-insensitive search across elements with id
    try {
        const allWithId = document.querySelectorAll('[id]');
        const lowerClean = cleanId.toLowerCase();
        const lowerBare = bareId.toLowerCase();
        for (let i = 0; i < allWithId.length; i++) {
            const idVal = allWithId[i].id.toLowerCase();
            if (idVal === lowerClean || idVal === lowerBare || idVal === `sec-${lowerClean}` || idVal === `col-${lowerClean}` || idVal === `sec-${lowerBare}` || idVal === `col-${lowerBare}`) {
                return allWithId[i];
            }
        }
    } catch (e) { }

    return null;
};

export const smoothScrollTo = (rawId, duration = 600, headerOffset = 80) => {
    if (typeof window === 'undefined') return false;
    const cleanId = String(rawId || '').trim().replace(/^#/, '');
    if (!cleanId) return false;

    if (cleanId === 'top' || cleanId === 'header') {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return true;
    }

    const executeScroll = (retryCount = 0) => {
        const target = findAnchorElement(cleanId);
        if (target) {
            const rect = target.getBoundingClientRect();
            const targetPosition = rect.top + window.pageYOffset - headerOffset;
            const startPosition = window.pageYOffset;
            const distance = targetPosition - startPosition;

            if (Math.abs(distance) < 2) return true;

            let startTime = null;
            const ease = t => t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1;

            const animation = (currentTime) => {
                if (startTime === null) startTime = currentTime;
                const elapsed = currentTime - startTime;
                const progress = Math.min(elapsed / duration, 1);
                window.scrollTo(0, startPosition + distance * ease(progress));
                if (progress < 1) {
                    requestAnimationFrame(animation);
                }
            };
            requestAnimationFrame(animation);
            return true;
        }

        if (retryCount < 5) {
            setTimeout(() => executeScroll(retryCount + 1), 100 * (retryCount + 1));
            return true;
        }

        return false;
    };

    return executeScroll(0);
};

export const isEmptyHtml = (html) => {
    if (!html || typeof html !== 'string') return true;
    const stripped = html
        .replace(/<br\s*\/?>/gi, '')
        .replace(/<\/?(p|div|span|font|b|i|u|strong|em|h[1-6])[^>]*>/gi, '')
        .replace(/&nbsp;/gi, ' ')
        .trim();
    return stripped === '' && !/<(img|video|iframe|svg|canvas)\b/i.test(html);
};

// ─── COLOR, OPACITY & GRADIENT HELPERS ───────────────────────────────────────
export const parseAlpha = (alpha, defaultVal = 1) => {
    if (alpha === undefined || alpha === null || alpha === '') return defaultVal;
    let num = typeof alpha === 'number' ? alpha : parseFloat(String(alpha).replace('%', ''));
    if (isNaN(num)) return defaultVal;
    if (num > 1) num = num / 100;
    return Math.max(0, Math.min(1, num));
};

export const colorToHex = (colorStr, fallback = '#3b82f6') => {
    if (!colorStr) return fallback;
    const str = String(colorStr).trim();
    if (str.startsWith('#')) {
        const hex = str.slice(1);
        if (hex.length === 3) return `#${hex.split('').map(c => c + c).join('')}`;
        if (hex.length >= 6) return `#${hex.substring(0, 6)}`;
    }
    const match = str.match(/rgba?\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
    if (match) {
        const r = parseInt(match[1], 10).toString(16).padStart(2, '0');
        const g = parseInt(match[2], 10).toString(16).padStart(2, '0');
        const b = parseInt(match[3], 10).toString(16).padStart(2, '0');
        return `#${r}${g}${b}`;
    }
    return fallback;
};

export const hexToRgba = (colorStr, alpha = 1) => {
    if (!colorStr || colorStr === 'transparent') return 'transparent';
    const numAlpha = parseAlpha(alpha, 1);

    // Check if rgba or rgb
    const rgbaMatch = String(colorStr).trim().match(/^rgba?\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)(?:\s*,\s*([\d.]+))?\s*\)$/i);
    if (rgbaMatch) {
        const r = parseInt(rgbaMatch[1], 10);
        const g = parseInt(rgbaMatch[2], 10);
        const b = parseInt(rgbaMatch[3], 10);
        const existingA = rgbaMatch[4] !== undefined ? parseFloat(rgbaMatch[4]) : 1;
        const finalA = Math.round(existingA * numAlpha * 1000) / 1000;
        return `rgba(${r}, ${g}, ${b}, ${finalA})`;
    }

    // Check if hex
    let hex = String(colorStr).trim();
    if (hex.startsWith('#')) {
        hex = hex.slice(1);
        if (hex.length === 3) {
            hex = hex.split('').map(c => c + c).join('');
        }
        if (hex.length === 6) {
            const r = parseInt(hex.substring(0, 2), 16) || 0;
            const g = parseInt(hex.substring(2, 4), 16) || 0;
            const b = parseInt(hex.substring(4, 6), 16) || 0;
            return `rgba(${r}, ${g}, ${b}, ${Math.round(numAlpha * 1000) / 1000})`;
        }
        if (hex.length === 8) {
            const r = parseInt(hex.substring(0, 2), 16) || 0;
            const g = parseInt(hex.substring(2, 4), 16) || 0;
            const b = parseInt(hex.substring(4, 6), 16) || 0;
            const hexA = (parseInt(hex.substring(6, 8), 16) || 255) / 255;
            return `rgba(${r}, ${g}, ${b}, ${Math.round(hexA * numAlpha * 1000) / 1000})`;
        }
    }

    const NAMED = {
        white: [255, 255, 255],
        black: [0, 0, 0],
        red: [239, 68, 68],
        blue: [59, 130, 246],
        gray: [100, 116, 139],
        grey: [100, 116, 139],
        slate: [30, 41, 59],
        green: [34, 197, 94],
        emerald: [16, 185, 129],
        amber: [245, 158, 11],
        purple: [168, 85, 247],
        indigo: [99, 102, 241]
    };
    const lower = String(colorStr).trim().toLowerCase();
    if (NAMED[lower]) {
        const [r, g, b] = NAMED[lower];
        return `rgba(${r}, ${g}, ${b}, ${Math.round(numAlpha * 1000) / 1000})`;
    }

    if (numAlpha >= 0.999) return colorStr;
    return colorStr;
};

export const GRADIENT_PRESETS = [
    { label: 'Sunset', start: '#ff7e5f', end: '#feb47b', angle: '135deg' },
    { label: 'Ocean', start: '#2b5876', end: '#4e4376', angle: '135deg' },
    { label: 'Midnight', start: '#0f172a', end: '#1e293b', angle: '180deg' },
    { label: 'Emerald', start: '#065f46', end: '#10b981', angle: '135deg' },
    { label: 'Velvet', start: '#4c1d95', end: '#7c3aed', angle: '135deg' },
    { label: 'Frosted Glass', start: '#ffffff', end: '#ffffff', angle: '135deg', startOpacity: 75, endOpacity: 25 },
    { label: 'Amber Fire', start: '#f59e0b', end: '#ef4444', angle: '90deg' },
    { label: 'Cyber Neon', start: '#06b6d4', end: '#3b82f6', angle: '135deg' },
    { label: 'Slate Dark', start: '#18181b', end: '#27272a', angle: '180deg' },
    { label: 'Fade Clear', start: '#0f172a', end: '#0f172a', angle: '180deg', startOpacity: 85, endOpacity: 0 },
    { label: 'Radial Glow', start: '#3b82f6', end: '#0f172a', angle: 'radial' }
];



